import { useLang } from "@/i18n/LanguageContext";
import { Check, MapPin } from "lucide-react";

const toneBg: Record<string, string> = {
  mint: "bg-mint",
  coral: "bg-coral",
  butter: "bg-butter",
  lavender: "bg-lavender",
  sky: "bg-sky-block",
};

const BrokerSection = () => {
  const { t } = useLang();
  return (
    <section id="marketplaces" className="py-24 md:py-32 bg-background">
      <div className="container">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-4">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">{t.broker.eyebrow}</span>
          </div>
          <div className="lg:col-span-8">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-extrabold leading-[1.05]">{t.broker.title}</h2>
            <p className="mt-5 text-lg md:text-xl text-muted-foreground max-w-2xl">{t.broker.desc}</p>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          <div className="rounded-3xl bg-secondary p-6 md:p-8">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> {t.broker.invitedTitle}
            </div>
            <ul className="mt-4 space-y-3">
              {t.broker.invitedSuppliers.map((s) => (
                <li key={s.name} className="flex items-center justify-between rounded-2xl bg-background px-4 py-3 shadow-card">
                  <div>
                    <div className="text-sm font-bold">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.tag}</div>
                  </div>
                  <span className={`text-[11px] font-bold rounded-full px-3 py-1 ${s.status === "active" ? "bg-mint text-mint-foreground" : "bg-butter text-butter-foreground"}`}>
                    {s.status === "active" ? "Actief" : "Uitgenodigd"}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-2xl bg-background p-4 shadow-card">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">{t.broker.marginLabel}</div>
                <div className="text-sm font-extrabold">15%</div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full w-[15%] bg-gradient-primary rounded-full" />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-lg bg-secondary p-2 text-center">5%</div>
                <div className="rounded-lg bg-primary text-primary-foreground p-2 text-center font-bold">15%</div>
                <div className="rounded-lg bg-secondary p-2 text-center">25%</div>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 content-start">
            {t.broker.storefronts.map((s) => (
              <div key={s.name} className={`rounded-3xl p-2 ${toneBg[s.tone]}`}>
                <div className="rounded-2xl bg-background overflow-hidden shadow-card">
                  <div className={`h-20 ${toneBg[s.tone]}`} />
                  <div className="p-4">
                    <div className="text-sm font-bold">{s.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.tag}</div>
                    <div className="mt-3 grid grid-cols-3 gap-1.5">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="aspect-square rounded-md bg-secondary" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <ul className="sm:col-span-2 grid sm:grid-cols-2 gap-3 mt-2">
              {t.broker.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0">
                    <Check className="h-3 w-3" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 rounded-3xl bg-mint p-6 md:p-8 text-mint-foreground">
            <h3 className="text-2xl md:text-3xl font-extrabold leading-tight">{t.broker.relationshipTitle}</h3>
            <p className="mt-3 text-sm md:text-base opacity-90">{t.broker.relationshipDesc}</p>
          </div>
          <div className="lg:col-span-3 rounded-3xl bg-secondary p-6 md:p-8">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.broker.scaleTitle}</div>
            <ul className="mt-4 grid sm:grid-cols-3 gap-3">
              {t.broker.scaleItems.map((s) => (
                <li key={s.t} className="rounded-2xl bg-background p-4 shadow-card">
                  <div className="text-sm font-extrabold">{s.t}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrokerSection;
